/**
 * The derived facts behind the exported review — Step 54.
 *
 * ONE derivation, read by two surfaces: the editor on the Review tab (which
 * shows each derived value beside the author's correction) and the document
 * itself. Two copies would drift, and the drift would be invisible — the
 * editor would show you one number and the PDF would print another.
 *
 * It lives in the UI rather than `@itsm/shared` because every value here is a
 * FORMATTED STRING in the reader's locale, which is a rendering concern; the
 * shared package owns the arithmetic (`incidentMetrics`, `formatDuration`)
 * and the list of facts that exist (`PIR_FACTS`).
 */
import { OUTAGE_KIND_LABEL, incidentMetrics } from '@itsm/shared';
import { fullTime } from '../../lib/time';
import type { MajorIncident } from './OutageTab';

export interface FactSources {
  ticket:
    | {
        status: string | null;
        priority: string;
        assigneeName?: string | null;
        groupName?: string | null;
        requester?: { name: string | null; email: string } | null;
        createdAt: number;
        firstRespondedAt?: number | null;
        resolvedAt?: number | null;
        closedAt?: number | null;
      }
    | undefined;
  mi: (MajorIncident & { managerName?: string | null }) | null;
  /** Present at all means the ticket had an SLA; the flag says whether it went. */
  slaClocks: { breachedAt?: number | null }[];
}

/** Every fact the document can print, already formatted. Missing values are a
 *  dash rather than an empty string, so a printed grid never has a hole in it
 *  that reads as a rendering fault. */
export function derivePirFacts({ ticket: t, mi: m, slaClocks }: FactSources): Record<string, string> {
  const time = (ms: number | null | undefined) => (ms ? fullTime(ms) : '—');
  const metrics = incidentMetrics({
    detectedAt: m?.detectedAt ?? null,
    createdAt: t?.createdAt ?? 0,
    acknowledgedAt: t?.firstRespondedAt ?? null,
    declaredAt: m?.declaredAt ?? null,
    stoodDownAt: m?.stoodDownAt ?? null,
    resolvedAt: t?.resolvedAt ?? null,
    closedAt: t?.closedAt ?? null,
  });
  return {
    status: t?.status ?? '—',
    priority: t?.priority ?? '—',
    outageKind: m?.outageKind ? OUTAGE_KIND_LABEL[m.outageKind] : '—',
    // No clocks at all is a different answer from "did not breach".
    slaBreached: slaClocks.length ? (slaClocks.some((c) => c.breachedAt) ? 'Yes' : 'No') : '—',
    customersImpacted: m?.customersImpacted == null ? 'Not counted' : m.customersImpacted.toLocaleString(),
    assignee: t?.assigneeName ?? 'Unassigned',
    manager: m?.managerName ?? '—',
    group: t?.groupName ?? '—',
    raisedBy: t?.requester?.name ?? t?.requester?.email ?? '—',
    detectedAt: time(m?.detectedAt),
    createdAt: time(t?.createdAt),
    declaredAt: time(m?.declaredAt),
    stoodDownAt: m?.stoodDownAt ? fullTime(m.stoodDownAt) : 'Not yet',
    resolvedAt: t?.resolvedAt ? fullTime(t.resolvedAt) : 'Not yet',
    mttd: metrics.mttd ?? '—',
    mtta: metrics.mtta ?? '—',
    mttr: metrics.mttr ?? '—',
    outage: metrics.outage ?? '—',
  };
}
